/**
 * The default sound pack: a low, cinematic swell — the Zimmer register.
 *
 * Same rendering strategy as the other packs (one AudioBuffer per pitch,
 * rendered once through an OfflineAudioContext at load), so the engine cannot
 * tell it apart from a sample pack. Only the timbre and the room differ.
 *
 * Four things do the heavy lifting, and they're all deliberate:
 *
 *   - **Weight without low frequencies.** Film-score brass lives an octave or
 *     more below where this game can go: a phone speaker rolls off hard under
 *     ~300Hz, so an actual low fundamental would simply vanish on the device
 *     most people play on. Instead a sub-octave partial is rendered at low
 *     amplitude *underneath* the root. The ear reconstructs the missing
 *     fundamental from the harmonic series, so the note reads as deep on a
 *     laptop and still reads as present on a phone.
 *   - **A slow swell, not an onset.** Attack is ~0.28s with the partials
 *     staggered behind it, so a note blooms rather than arrives. This is the
 *     single biggest difference from the harp pack.
 *   - **A filter that opens as it swells.** Brass gets brighter as it gets
 *     louder; a static lowpass sounds like a synth pad, a moving one sounds
 *     like an instrument being pushed. The sweep is per-note and rendered in,
 *     not applied live.
 *   - **An ensemble, not a voice.** Three detuned pairs beating against each
 *     other at slightly different rates is what separates "a section" from
 *     "an oscillator".
 */

import type { SoundPack } from "../pack.ts";
import { normalize, offlineContext } from "../render.ts";

/** Long enough to bloom, short enough that a fast drag still reads as separate notes. */
const ATTACK_SECONDS = 0.28;

interface Partial {
  /** Multiple of the fundamental. 0.5 is the sub-octave. */
  ratio: number;
  gain: number;
  /** Fraction of the note's decay time this partial gets. */
  decay: number;
  /** Cents of detune, for ensemble beating. */
  detune: number;
  /** Seconds of extra delay before this partial starts its own swell. */
  delay?: number;
}

/**
 * A brass-ish stack: strong odd harmonics, a soft sub, and enough upper
 * partials to survive a small speaker. The detune values are intentionally
 * un-symmetric so the beating never settles into one obvious rate.
 */
const PARTIALS: readonly Partial[] = [
  // Sub-octave: the perceived weight. Quiet on purpose — it is felt, not heard.
  { ratio: 0.5, gain: 0.42, decay: 1, detune: 0 },
  // The root, as a detuned pair.
  { ratio: 1, gain: 1, decay: 1, detune: -6 },
  { ratio: 1, gain: 0.82, decay: 1, detune: 7 },
  // Fifth above — the open-fifth colour the win chord also leans on.
  { ratio: 1.5, gain: 0.3, decay: 0.9, detune: 4, delay: 0.05 },
  // Octave pair.
  { ratio: 2, gain: 0.38, decay: 0.85, detune: -4 },
  { ratio: 2, gain: 0.26, decay: 0.85, detune: 9, delay: 0.04 },
  // Upper harmonics: brass edge, and what keeps the note audible on a phone.
  { ratio: 3, gain: 0.16, decay: 0.6, detune: -8, delay: 0.08 },
  { ratio: 4, gain: 0.09, decay: 0.45, detune: 6, delay: 0.11 },
  { ratio: 5, gain: 0.05, decay: 0.3, detune: -5, delay: 0.14 },
];

/** Long tails — the point is that notes overlap into a chord as the path grows. */
function noteDuration(freq: number): number {
  return Math.min(5.5, Math.max(3.2, 5.8 * (220 / freq) ** 0.28));
}

/**
 * A breath of filtered noise under the swell. Not a transient — it fades in
 * with the note and back out, which is closer to a section taking a breath
 * than to anything being struck.
 */
function addBreath(ctx: OfflineAudioContext, freq: number, out: AudioNode): void {
  const length = Math.ceil(0.9 * ctx.sampleRate);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;

  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const band = ctx.createBiquadFilter();
  band.type = "bandpass";
  band.frequency.value = Math.min(freq * 1.8, ctx.sampleRate / 2.2);
  band.Q.value = 0.7;

  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, 0);
  env.gain.linearRampToValueAtTime(0.026, ATTACK_SECONDS);
  env.gain.exponentialRampToValueAtTime(0.0001, 0.9);

  source.connect(band).connect(env).connect(out);
  source.start(0);
}

async function renderNote(sampleRate: number, freq: number): Promise<AudioBuffer> {
  const duration = noteDuration(freq);
  const ctx = offlineContext(Math.ceil(duration * sampleRate), sampleRate);

  // Tail guard: fade the last 120ms so the buffer never ends on a non-zero
  // sample, which would click every time the note is played.
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.85, 0);
  master.gain.setValueAtTime(0.85, Math.max(0, duration - 0.12));
  master.gain.linearRampToValueAtTime(0, duration);

  // The brass sweep: opens with the swell, then slowly closes as the note
  // decays, so the tail darkens instead of thinning out.
  const body = ctx.createBiquadFilter();
  body.type = "lowpass";
  body.Q.value = 0.9;
  const peak = Math.min(freq * 8, sampleRate * 0.42);
  body.frequency.setValueAtTime(Math.min(freq * 1.6, peak), 0);
  body.frequency.linearRampToValueAtTime(peak, ATTACK_SECONDS * 1.4);
  body.frequency.exponentialRampToValueAtTime(Math.max(freq * 2, 220), duration);

  // Keep the sub from turning to mud on a laptop speaker without gutting it.
  const rumbleCut = ctx.createBiquadFilter();
  rumbleCut.type = "highpass";
  rumbleCut.frequency.value = 55;

  body.connect(rumbleCut).connect(master).connect(ctx.destination);

  for (const partial of PARTIALS) {
    const osc = ctx.createOscillator();
    // Triangle for the sub (clean weight), sine above (no fizz when stacked).
    osc.type = partial.ratio < 1 ? "triangle" : "sine";
    osc.detune.value = partial.detune;
    osc.frequency.value = freq * partial.ratio;

    const start = partial.delay ?? 0;
    const env = ctx.createGain();
    const decay = Math.max(0.2, (duration - ATTACK_SECONDS - start) * partial.decay);
    env.gain.setValueAtTime(0.0001, 0);
    env.gain.setValueAtTime(0.0001, start);
    // Linear swell in, exponential fall away — the asymmetry is the "bloom".
    env.gain.linearRampToValueAtTime(partial.gain, start + ATTACK_SECONDS);
    env.gain.exponentialRampToValueAtTime(0.0001, start + ATTACK_SECONDS + decay);

    osc.connect(env).connect(body);
    osc.start(0);
    osc.stop(duration);
  }

  addBreath(ctx, freq, body);
  const rendered = await ctx.startRendering();
  normalize(rendered, 0.9);
  return rendered;
}

export const cinematicPack: SoundPack = {
  id: "cinematic",
  name: "Cinematic",
  // A hall rather than a room, and a heavier reverb send: the tail is as much
  // of the sound as the note is.
  mix: { gain: 0.82, reverb: 0.62, delay: 0.2, reverbSeconds: 4.5 },
  load(sampleRate, frequencies) {
    return Promise.all(frequencies.map((freq) => renderNote(sampleRate, freq)));
  },
};
